import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import {
  Archive,
  ArrowLeft,
  CheckCheck,
  Filter,
  History,
  Lock,
  RefreshCw,
  Bug,
  Paperclip,
  Download,
  FileText,
  ExternalLink,
  Wrench,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetBody,
} from '@/components/ui/Sheet';
import {
  DataTable,
  arrIncludesSome,
  type FacetedFilter,
} from '@/components/ui/DataTable';
import { formatCurrency, formatDate, formatRelative } from '@/lib/formatters';
import {
  useChartOfAccounts,
  useQboHistoricalTransactions,
  useQboHistoricalAttachments,
  useSyncQboHistory,
  useSyncQboHistoryAttachments,
  getQboHistoricalReceiptUrl,
  type QboHistorySyncDiagnostics,
} from '@/features/financials/hooks';
import {
  isWithinDrilldownRange,
  useTxDrilldownFilter,
} from '@/features/financials/transactionsDrilldown';
import type { Tables } from '@/types/database';

type HistoryRow = Tables<'qbo_historical_transactions'>;
type AttachmentRow = Tables<'qbo_historical_attachments'>;

interface LastSyncResult {
  rows: number;
  window: { start_date: string; end_date: string } | null;
  diagnostics: QboHistorySyncDiagnostics | null;
}

/**
 * Read-only history tab. Mirrors the QuickBooks bank-register ledger for the
 * pre-cutover period (2025-05-01 → 2026-04-30 by default). One row per QBO
 * TransactionList posting line. No edit affordances anywhere — clicking a
 * row does nothing, the QB Category column is plain text, and there's no
 * "Post to QBO" because this data already IS in QBO.
 *
 * Refreshing pulls from QuickBooks on demand. The sync wipes-and-reloads
 * the cache, so a re-run is the right action whenever the operator edits
 * a historical entry in QBO and wants Atlas to reflect it.
 */
export function QboHistoryTab() {
  const navigate = useNavigate();
  const { data, isLoading, dataUpdatedAt } = useQboHistoricalTransactions();
  const { data: attachmentsData } = useQboHistoricalAttachments();
  const { data: coa } = useChartOfAccounts();
  const syncHistory = useSyncQboHistory();
  const syncAttachments = useSyncQboHistoryAttachments();

  // P&L drill-down: same URL contract as the Live tab, applied here as
  // a `split_account_name` ∈ {names} pre-filter. The contract uses
  // chart_of_accounts.id (which qbo_historical_transactions doesn't
  // carry), so we resolve ids → names via the already-loaded CoA
  // before filtering. Date bounds work as-is against `posted_at`.
  const drilldown = useTxDrilldownFilter();
  const drilldownAccountNames = useMemo(() => {
    if (!drilldown.hasFilter || drilldown.accountIds.size === 0) return null;
    if (!coa) return null;
    const names = new Set<string>();
    for (const a of coa) {
      if (drilldown.accountIds.has(a.id)) names.add(a.name);
    }
    return names;
  }, [coa, drilldown.hasFilter, drilldown.accountIds]);

  // Retain the most recent sync result so we can show diagnostics for
  // 0-row pulls (column shape, raw row count, sample row) without forcing
  // the operator to scrape Edge Function logs.
  const [lastSync, setLastSync] = useState<LastSyncResult | null>(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);

  // Receipt-preview state. We open a Sheet showing the file inline (image
  // or PDF) and lazily mint a signed URL on demand — the bucket is
  // private so direct URLs aren't useful.
  const [previewAttachment, setPreviewAttachment] = useState<AttachmentRow | null>(null);

  const rows = useMemo<HistoryRow[]>(() => data ?? [], [data]);
  const attachmentsByTxnId = attachmentsData?.byTxnId;
  const totalAttachments = attachmentsData?.rows.length ?? 0;

  // Same pre-filter pattern as the Live tab's `visibleRows`: applied as
  // an extra layer on top of the dataset before TanStack's own filter /
  // sort / paginate runs. Skips the work entirely when no drill-down
  // is active (the common case).
  const visibleRows = useMemo<HistoryRow[]>(() => {
    if (!drilldown.hasFilter) return rows;
    return rows.filter((r) => {
      if (drilldownAccountNames && drilldownAccountNames.size > 0) {
        if (
          !r.split_account_name ||
          !drilldownAccountNames.has(r.split_account_name)
        ) {
          return false;
        }
      }
      return isWithinDrilldownRange(r.posted_at, drilldown.from, drilldown.to);
    });
  }, [rows, drilldown, drilldownAccountNames]);

  const onSync = async () => {
    try {
      const res = await syncHistory.mutateAsync(undefined);
      setLastSync({
        rows: res.rows ?? 0,
        window: res.window ?? null,
        diagnostics: res.diagnostics ?? null,
      });
      // Auto-open diagnostics when the pull comes back empty so the
      // operator doesn't have to remember to click.
      if ((res.rows ?? 0) === 0) setShowDiagnostics(true);
      const n = res.rows ?? 0;
      if (n === 0) {
        toast.warning(
          `QuickBooks returned 0 rows for ${res.window?.start_date} \u2192 ${res.window?.end_date}. ` +
            `See the diagnostic panel below.`,
        );
      } else {
        toast.success(`Pulled ${n} historical row${n === 1 ? '' : 's'} from QuickBooks.`);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Sync failed';
      toast.error(msg);
    }
  };

  const onSyncReceipts = async () => {
    try {
      const res = await syncAttachments.mutateAsync(undefined);
      if (res.budget_hit) {
        toast.warning(
          res.message ??
            `Downloaded ${res.downloads}. Click "Pull receipts" again to continue.`,
        );
      } else if (res.downloads === 0 && res.new_links === 0 && res.skipped_existing === 0) {
        toast.info(res.message ?? 'No matching receipts found in QuickBooks.');
      } else {
        toast.success(res.message ?? 'Receipts synced.');
      }
      if (res.total_failures && res.total_failures > 0) {
        toast.error(
          `${res.total_failures} attachment(s) failed — see the QBO logs in Supabase.`,
        );
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Receipt sync failed';
      toast.error(msg);
    }
  };

  // Re-fetch bytes for every cached Attachable and overwrite the file in
  // Storage. Use this once after the URL-string-instead-of-bytes bug was
  // fixed — pre-fix uploads were tiny URL blobs that won't open. With
  // ~200 files per run, the operator may need to click this button a few
  // times if the org has more receipts than the per-run budget.
  const onRepairReceipts = async () => {
    if (
      !window.confirm(
        'Re-download every receipt from QuickBooks and overwrite the cached file in Storage? Use this if downloaded receipts are showing up corrupt.',
      )
    ) {
      return;
    }
    try {
      const res = await syncAttachments.mutateAsync({ redownloadExisting: true });
      if (res.budget_hit) {
        toast.warning(
          res.message ??
            `Repaired ${res.redownloaded_existing ?? 0}. Click "Repair receipts" again to continue.`,
        );
      } else if (
        (res.redownloaded_existing ?? 0) === 0 &&
        res.downloads === 0 &&
        res.new_links === 0
      ) {
        toast.info(res.message ?? 'No receipts needed repair.');
      } else {
        toast.success(res.message ?? 'Receipts repaired.');
      }
      if (res.total_failures && res.total_failures > 0) {
        toast.error(
          `${res.total_failures} attachment(s) failed — see the QBO logs in Supabase.`,
        );
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Receipt repair failed';
      toast.error(msg);
    }
  };

  // The "newest synced_at" doubles as a freshness indicator. Every row in a
  // given org is updated together (wipe-and-reload), so any row's
  // synced_at is representative.
  const lastSyncedAt = rows[0]?.synced_at ?? null;

  // -----------------------------------------------------------------------
  // Columns — all display-only. No inline editors, no row click navigation.
  // -----------------------------------------------------------------------
  const columns = useMemo<ColumnDef<HistoryRow>[]>(
    () => [
      {
        id: 'posted_at',
        accessorKey: 'posted_at',
        header: 'Date',
        sortingFn: 'datetime',
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted-foreground">
            {formatDate(row.original.posted_at)}
          </span>
        ),
      },
      {
        id: 'qbo_txn_type',
        accessorKey: 'qbo_txn_type',
        header: 'Type',
        filterFn: arrIncludesSome,
        cell: ({ row }) => (
          <Badge variant="outline" className="font-normal">
            {row.original.qbo_txn_type}
          </Badge>
        ),
      },
      {
        id: 'doc_number',
        accessorKey: 'doc_number',
        header: 'Doc #',
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.doc_number ?? '—'}
          </span>
        ),
      },
      {
        id: 'account_name',
        accessorKey: 'account_name',
        header: 'Account',
        filterFn: arrIncludesSome,
        cell: ({ row }) => <span>{row.original.account_name}</span>,
      },
      {
        id: 'payee_name',
        accessorKey: 'payee_name',
        header: 'Payee',
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {row.original.payee_name ?? '—'}
          </span>
        ),
      },
      {
        id: 'split_account_name',
        accessorKey: 'split_account_name',
        header: 'Category',
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {row.original.split_account_name ?? '—'}
          </span>
        ),
      },
      {
        id: 'memo',
        accessorKey: 'memo',
        header: 'Memo',
        cell: ({ row }) => (
          <span className="text-muted-foreground">{row.original.memo ?? '—'}</span>
        ),
      },
      {
        id: 'is_cleared',
        accessorKey: 'is_cleared',
        header: 'Cleared',
        filterFn: arrIncludesSome,
        cell: ({ row }) => {
          const c = row.original.is_cleared;
          if (c === 'R') {
            return (
              <span className="inline-flex items-center gap-1 text-xs text-bright-moss-dark">
                <CheckCheck className="h-3 w-3" />
                Reconciled
              </span>
            );
          }
          if (c === 'C') {
            return (
              <span className="inline-flex items-center gap-1 text-xs text-atmosphere-dark">
                <CheckCheck className="h-3 w-3" />
                Cleared
              </span>
            );
          }
          return <span className="text-xs text-muted-foreground">—</span>;
        },
      },
      {
        id: 'amount_cents',
        accessorKey: 'amount_cents',
        header: () => <span className="block text-right">Amount</span>,
        sortingFn: 'basic',
        cell: ({ row }) => {
          const cents = row.original.amount_cents;
          const positive = cents >= 0;
          return (
            <span
              className={
                'block text-right tabular-nums ' +
                (positive ? 'text-bright-moss-dark' : 'text-foreground')
              }
            >
              {positive ? '+' : '−'}
              {formatCurrency(Math.abs(cents))}
            </span>
          );
        },
      },
      {
        id: 'receipt',
        header: () => <span className="block text-center">Receipt</span>,
        // No accessor — we look up by qbo_txn_id at render time so the column
        // stays in sync with the attachments query without re-running the
        // transactions query.
        enableSorting: false,
        cell: ({ row }) => {
          const txnId = row.original.qbo_txn_id;
          if (!txnId) {
            return <span className="block text-center text-xs text-muted-foreground">—</span>;
          }
          const atts = attachmentsByTxnId?.get(txnId) ?? [];
          if (atts.length === 0) {
            return <span className="block text-center text-xs text-muted-foreground">—</span>;
          }
          // Multiple attachments → render up to 3 separate paperclip
          // buttons stacked horizontally. Past that, fall back to a count
          // badge that opens the first attachment (we'll switch to a
          // multi-file picker if anyone ever has >3 receipts on a row).
          // Stop propagation so clicking a paperclip doesn't also fire
          // the row's navigate-to-detail handler.
          const handleClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            setPreviewAttachment(atts[0]);
          };
          if (atts.length === 1) {
            return (
              <div className="flex justify-center">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 gap-1 px-2 text-xs"
                  onClick={handleClick}
                  title={atts[0].file_name}
                >
                  <Paperclip className="h-3.5 w-3.5" />
                </Button>
              </div>
            );
          }
          return (
            <div className="flex justify-center">
              <Button
                size="sm"
                variant="ghost"
                className="h-7 gap-1 px-2 text-xs"
                onClick={handleClick}
                title={`${atts.length} attachments`}
              >
                <Paperclip className="h-3.5 w-3.5" />
                <span className="tabular-nums">{atts.length}</span>
              </Button>
            </div>
          );
        },
      },
    ],
    [attachmentsByTxnId],
  );

  const facetedFilters: FacetedFilter[] = useMemo(
    () => [
      { columnId: 'qbo_txn_type', title: 'Type' },
      { columnId: 'account_name', title: 'Account' },
      {
        columnId: 'is_cleared',
        title: 'Cleared',
        options: [
          { value: 'R', label: 'Reconciled' },
          { value: 'C', label: 'Cleared' },
          { value: '', label: 'Neither' },
        ],
      },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      {/* Read-only banner — makes it visually unmissable that this surface
          is different from the live Transactions tab. */}
      <div className="flex flex-col gap-3 rounded-lg border border-border bg-secondary/40 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <Lock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="space-y-0.5">
            <p className="font-medium text-foreground">
              Read-only · sourced from QuickBooks
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {lastSyncedAt && (
            <span className="text-xs text-muted-foreground">
              Updated {formatRelative(lastSyncedAt)}
            </span>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={onSync}
            disabled={syncHistory.isPending || syncAttachments.isPending}
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${syncHistory.isPending ? 'animate-spin' : ''}`}
            />
            {syncHistory.isPending ? 'Pulling…' : 'Pull from QuickBooks'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onSyncReceipts}
            disabled={
              syncHistory.isPending ||
              syncAttachments.isPending ||
              rows.length === 0
            }
            title={
              rows.length === 0
                ? 'Pull transactions from QuickBooks first'
                : 'Download receipts attached to historical transactions'
            }
          >
            <Paperclip
              className={`h-3.5 w-3.5 ${syncAttachments.isPending ? 'animate-pulse' : ''}`}
            />
            {syncAttachments.isPending ? 'Pulling receipts…' : 'Pull receipts'}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onRepairReceipts}
            disabled={
              syncHistory.isPending ||
              syncAttachments.isPending ||
              totalAttachments === 0
            }
            title={
              totalAttachments === 0
                ? 'Pull receipts at least once before repairing'
                : 'Re-download every cached receipt from QuickBooks. Use this if downloaded files are showing up corrupt.'
            }
          >
            <Wrench
              className={`h-3.5 w-3.5 ${syncAttachments.isPending ? 'animate-pulse' : ''}`}
            />
            Repair receipts
          </Button>
        </div>
      </div>

      {lastSync && lastSync.diagnostics && (
        <div className="rounded-lg border border-border bg-background">
          <button
            type="button"
            onClick={() => setShowDiagnostics((v) => !v)}
            className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm hover:bg-secondary/40"
          >
            <span className="inline-flex items-center gap-2">
              <Bug className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Last pull diagnostics</span>
              <span className="text-xs text-muted-foreground">
                {lastSync.rows} parsed · {lastSync.diagnostics.raw_data_row_count} raw rows ·
                {' '}
                {lastSync.diagnostics.column_count} columns
                {lastSync.diagnostics.cleared_call
                  ? lastSync.diagnostics.cleared_call.ok
                    ? ` · is_cleared mapped on ${lastSync.diagnostics.cleared_call.txn_ids_mapped} txns`
                    : ` · is_cleared call failed`
                  : ''}
                {lastSync.window
                  ? ` · ${lastSync.window.start_date} → ${lastSync.window.end_date}`
                  : ''}
              </span>
            </span>
            <span className="text-xs text-muted-foreground">
              {showDiagnostics ? 'Hide' : 'Show'}
            </span>
          </button>
          {showDiagnostics && (
            <div className="space-y-3 border-t border-border px-4 py-4 text-xs">
              <div>
                <p className="mb-1 font-medium text-foreground">QBO report header</p>
                <pre className="overflow-x-auto rounded border border-border bg-secondary/30 p-2 text-[11px]">
                  {JSON.stringify(lastSync.diagnostics.header, null, 2)}
                </pre>
              </div>
              <div>
                <p className="mb-1 font-medium text-foreground">
                  Columns returned ({lastSync.diagnostics.columns.length})
                </p>
                <div className="overflow-x-auto rounded border border-border">
                  <table className="min-w-full text-[11px]">
                    <thead className="bg-secondary/40">
                      <tr>
                        <th className="px-2 py-1 text-left font-medium">#</th>
                        <th className="px-2 py-1 text-left font-medium">ColTitle</th>
                        <th className="px-2 py-1 text-left font-medium">ColType</th>
                        <th className="px-2 py-1 text-left font-medium">ColKey</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lastSync.diagnostics.columns.map((c, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="px-2 py-1 text-muted-foreground">{i}</td>
                          <td className="px-2 py-1">{c.ColTitle ?? '—'}</td>
                          <td className="px-2 py-1 text-muted-foreground">{c.ColType ?? '—'}</td>
                          <td className="px-2 py-1 font-mono">{c.ColKey ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <p className="mb-1 font-medium text-foreground">First data row (sample)</p>
                {lastSync.diagnostics.sample_row ? (
                  <pre className="overflow-x-auto rounded border border-border bg-secondary/30 p-2 text-[11px]">
                    {JSON.stringify(lastSync.diagnostics.sample_row, null, 2)}
                  </pre>
                ) : (
                  <p className="text-muted-foreground">
                    No data rows between {lastSync.window?.start_date} and{' '}
                    {lastSync.window?.end_date}.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <Card>
        <CardContent className="pt-6">
          {drilldown.hasFilter && (
            // Drill-down banner. Mirrors the Live tab's banner — atmosphere
            // tint to distinguish from the warning-tinted reset/diagnostics
            // chrome elsewhere on this page, and a "Show all" affordance
            // that strips the drill-down params while preserving `tab`.
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-atmosphere-dark/30 bg-atmosphere-dark/5 px-3 py-2 text-sm">
              <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-0.5 text-atmosphere-dark">
                <Filter className="h-3.5 w-3.5" />
                Drilled in from {drilldown.source ?? 'P&L'}
                {drilldown.accountLabel && (
                  <span className="font-medium">
                    {drilldown.accountLabel}
                  </span>
                )}
                {drilldown.monthLabel && (
                  <span className="text-atmosphere-dark/80">
                    · {drilldown.monthLabel}
                  </span>
                )}
                <span className="text-xs text-muted-foreground">
                  ({visibleRows.length} row
                  {visibleRows.length === 1 ? '' : 's'})
                </span>
              </span>
              <span className="inline-flex items-center gap-1">
                {drilldown.back && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(drilldown.back!)}
                    className="h-7 gap-1 px-2 text-xs"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Back to {drilldown.source ?? 'summary'}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={drilldown.clear}
                  className="h-7 gap-1 px-2 text-xs"
                >
                  <X className="h-3.5 w-3.5" />
                  Show all
                </Button>
              </span>
            </div>
          )}
          <DataTable<HistoryRow, unknown>
            data={visibleRows}
            columns={columns}
            isLoading={isLoading}
            getRowId={(r) => r.id}
            tableId="qbo-history"
            enableColumnResizing
            // Doc # and Cleared are useful but noisy by default; hidden
            // on first encounter and toggleable via the "View" menu.
            // Persisted per-user in localStorage thereafter.
            initialColumnVisibility={{
              doc_number: false,
              is_cleared: false,
            }}
            onRowClick={(r) => navigate(`/financials/transactions/history/${r.id}`)}
            searchPlaceholder="Search account, payee, memo, doc#…"
            facetedFilters={facetedFilters}
            initialPageSize={50}
            pageSizes={[25, 50, 100, 200, 500]}
            // Mobile card layout — payee on top, account + date in
            // the subtitle, amount on the right with QBO's
            // sign-aware tone, and a small badge row showing
            // transaction type + (when present) a paperclip badge
            // for receipts.
            mobileCard={{
              title: (r) =>
                r.payee_name ?? r.split_account_name ?? r.account_name,
              subtitle: (r) => {
                const parts: string[] = [formatDate(r.posted_at), r.account_name];
                if (r.memo) parts.push(r.memo);
                return parts.join(' · ');
              },
              badges: (r) => {
                const out: React.ReactNode[] = [
                  <Badge key="type" variant="outline">
                    {r.qbo_txn_type}
                  </Badge>,
                ];
                if (r.is_cleared === 'R') {
                  out.push(
                    <Badge key="cleared" variant="success">
                      Reconciled
                    </Badge>,
                  );
                } else if (r.is_cleared === 'C') {
                  out.push(
                    <Badge key="cleared" variant="info">
                      Cleared
                    </Badge>,
                  );
                }
                if (r.qbo_txn_id && (attachmentsByTxnId?.get(r.qbo_txn_id)?.length ?? 0) > 0) {
                  out.push(
                    <Badge key="att" variant="muted" className="gap-1">
                      <Paperclip className="h-3 w-3" />
                      {attachmentsByTxnId?.get(r.qbo_txn_id)?.length}
                    </Badge>,
                  );
                }
                return out;
              },
              amount: (r) => ({
                value: `${r.amount_cents >= 0 ? '+' : '−'}${formatCurrency(Math.abs(r.amount_cents))}`,
                tone: r.amount_cents >= 0 ? 'positive' : 'neutral',
              }),
            }}
            emptyState={
              syncHistory.isPending ? (
                <EmptyState
                  icon={History}
                  title="Pulling QuickBooks history…"
                />
              ) : drilldown.hasFilter && rows.length > 0 ? (
                // Underlying dataset has rows but the drill-down filtered
                // them all out — most likely an account name mismatch
                // between Atlas's CoA and QBO's split_account_name on
                // these historical lines. Worth surfacing because the
                // operator's other recourse (clearing the filter) is in
                // the banner above, but the empty-state message keeps
                // them oriented.
                <EmptyState
                  icon={Archive}
                  title="No historical rows in this slice"
                />
              ) : (
                <EmptyState
                  icon={Archive}
                  title="No historical transactions yet"
                />
              )
            }
          />
          {!isLoading && rows.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2 text-xs text-muted-foreground">
              <Badge variant="outline">
                {drilldown.hasFilter
                  ? `${visibleRows.length} of ${rows.length} rows`
                  : `${rows.length} rows`}
              </Badge>
              {totalAttachments > 0 && (
                <Badge variant="outline" className="gap-1">
                  <Paperclip className="h-3 w-3" />
                  {totalAttachments} receipt{totalAttachments === 1 ? '' : 's'}
                </Badge>
              )}
              {dataUpdatedAt > 0 && (
                <Badge variant="outline">
                  Cached {formatRelative(new Date(dataUpdatedAt).toISOString())}
                </Badge>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <ReceiptPreviewSheet
        attachment={previewAttachment}
        onClose={() => setPreviewAttachment(null)}
      />
    </div>
  );
}

// -----------------------------------------------------------------------------
// ReceiptPreviewSheet — opens when the user clicks a paperclip in the table.
// Mints a short-lived signed URL for the file in qbo-historical-receipts and
// renders it inline if it's an image or PDF; everything else gets a
// download-only fallback.
// -----------------------------------------------------------------------------
function ReceiptPreviewSheet({
  attachment,
  onClose,
}: {
  attachment: AttachmentRow | null;
  onClose: () => void;
}) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);

  useEffect(() => {
    setSignedUrl(null);
    setUrlError(null);
    if (!attachment) return;
    let cancelled = false;
    (async () => {
      const url = await getQboHistoricalReceiptUrl(attachment.storage_path);
      if (cancelled) return;
      if (!url) {
        setUrlError(
          'Could not generate a signed URL for this receipt. Make sure the file exists in the qbo-historical-receipts bucket.',
        );
        return;
      }
      setSignedUrl(url);
    })();
    return () => {
      cancelled = true;
    };
  }, [attachment]);

  const isImage = (attachment?.content_type ?? '').startsWith('image/');
  const isPdf = (attachment?.content_type ?? '') === 'application/pdf';

  return (
    <Sheet open={!!attachment} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            {attachment?.file_name ?? 'Receipt'}
          </SheetTitle>
          <SheetDescription className="space-x-2 text-xs">
            <span>{attachment?.content_type ?? 'unknown type'}</span>
            {attachment?.size_bytes ? (
              <span>· {formatBytes(attachment.size_bytes)}</span>
            ) : null}
            {attachment?.category ? <span>· {attachment.category}</span> : null}
          </SheetDescription>
        </SheetHeader>
        <SheetBody className="space-y-3">
          {attachment?.note && (
            <p className="rounded-md border border-border bg-secondary/30 px-3 py-2 text-xs text-muted-foreground">
              {attachment.note}
            </p>
          )}
          {urlError && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {urlError}
            </p>
          )}
          {!signedUrl && !urlError && (
            <p className="text-xs text-muted-foreground">Loading…</p>
          )}
          {signedUrl && (
            <>
              {isImage && (
                <img
                  src={signedUrl}
                  alt={attachment?.file_name ?? 'Receipt'}
                  className="max-h-[70vh] w-full rounded-md border border-border object-contain"
                />
              )}
              {isPdf && (
                <iframe
                  src={signedUrl}
                  title={attachment?.file_name ?? 'Receipt'}
                  className="h-[70vh] w-full rounded-md border border-border"
                />
              )}
              {!isImage && !isPdf && (
                <p className="text-xs text-muted-foreground">
                  Preview unavailable for this file type.
                </p>
              )}
              <div className="flex gap-2 pt-2">
                <Button
                  size="sm"
                  variant="outline"
                  asChild
                >
                  <a href={signedUrl} target="_blank" rel="noreferrer">
                    <ExternalLink className="h-3.5 w-3.5" />
                    Open in new tab
                  </a>
                </Button>
                <Button size="sm" variant="outline" asChild>
                  <a
                    href={signedUrl}
                    download={attachment?.file_name ?? 'receipt'}
                  >
                    <Download className="h-3.5 w-3.5" />
                    Download
                  </a>
                </Button>
              </div>
            </>
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}

// Tiny human-readable byte formatter. We don't have a shared one in
// @/lib/formatters and the receipt sheet is the only consumer, so it lives
// here.
function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
}
